/**
 * Cuanta plata hay en mercaderia, y en que.
 *
 * LA REGLA QUE ORDENA TODO ESTE ARCHIVO
 * -------------------------------------
 * El inventario vale en pesos Y vale en dolares, y esos dos numeros NO SE
 * SUMAN. No son dos plata distintas: son la misma mercaderia leida con dos
 * varas. `$ 40.000.000` y `US$ 25.800` son el mismo estante. Sumarlos da un
 * numero que no existe en ningun lado.
 *
 * Por eso hay dos tarjetas separadas y no una con un total. La conversion la
 * hace la base (`v_stock_valorizado` usa `cotizacion_vigente()`), asi que el
 * numero en dolares no es una estimacion del frontend: es el mismo dolar con el
 * que se repricia el catalogo.
 *
 * VALORIZADO A COSTO DE REPOSICION, NO A LO QUE SE PAGO
 * -----------------------------------------------------
 * La vista usa el costo de reposicion actual. Contesta "cuanto me saldria
 * reponer todo esto hoy", que es la pregunta util cuando hay inflacion, y no
 * "cuanto pague en su momento", que con seis meses de diferencia no significa
 * nada.
 *
 * LA TABLA LISTA LO QUE HAY, NO EL CATALOGO
 * -----------------------------------------
 * Los productos en cero no entran: son renglones de la lista de precios, y
 * mezclados con la mercaderia obligaban a saltear dos de cada tres filas para
 * contar. Quien los pide los tiene igual —el chip «Sin stock» los trae— y el
 * catalogo entero vive en Catalogo. El recorte lo hace la consulta
 * (`con_stock`), no la pantalla, para que el contador del pie y «Ver mas» digan
 * la verdad.
 *
 * SEIS COLUMNAS
 * -------------
 * Producto (con codigo, familia y proveedor debajo), Stock (con minimo e ideal
 * debajo), Estado, Costo unitario, Vale y acciones (06 §3.6). "Vale" muestra la
 * valorizacion en la moneda del costo del producto, con chip porque la columna
 * mezcla monedas; la otra lectura va como segunda linea en chico. La familia
 * ademas es un filtro de la barra.
 *
 * El pie de tabla no suma la columna "Vale" a proposito: sumar pesos de unos
 * productos con dolares de otros da un numero que no existe. Los totales de
 * arriba son el stock ENTERO leido en cada moneda, que es otra cosa (06 §3.5).
 */
import type { ReactNode } from "react";
import { ArrowDownToLine, ClipboardCheck, Info, PackageX, TriangleAlert, X } from "lucide-react";
import { formatearNumero } from "@tora/shared";

import { Importe } from "@/components/money";
import { mostrarCantidad } from "./Campos";
import { ChipsEstado } from "./ChipsEstado";
import { nombreCompleto } from "./nombre";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import { Desplegable } from "@/components/ui/desplegable";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Progreso } from "@/components/ui/progreso";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  aNumero,
  type EstadoStock,
  type FilaStock,
  type Rubro,
  type StockPorFamilia,
} from "@/lib/tipos";

import { SECCIONES, rubrosDeSeccion, type Seccion } from "./secciones";

import {
  AYUDA_ESTADO,
  ETIQUETA_ESTADO,
  VARIANTE_ESTADO,
  gananciaPotencial,
  plataPorSeccion,
  type TotalesStock,
} from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// La cinta de valor
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cuanta plata hay en mercaderia, en un renglon.
 *
 * ANTES ERAN TRES TARJETAS Y UN PARRAFO
 * -------------------------------------
 * Dos KPIs de plata (pesos y dolares), un tercero con las unidades y abajo un
 * parrafo de dos lineas que explicaba que los dos primeros son el mismo stock.
 * Cuatro bloques para una sola pregunta, y arriba de otro bloque —el desglose
 * por seccion— que contesta la misma pregunta partida. La tabla, que es lo que
 * se usa todos los dias, arrancaba a media pantalla; en el celular habia que
 * pasar dos pantallas enteras para ver el primer producto.
 *
 * Ahora es UNA tarjeta de tres renglones: los dos numeros grandes (que son el
 * mismo stock leido con dos varas), las unidades a la derecha, y lo demas —la
 * cotizacion con la que se convirtio, el valor a precio de venta y la ganancia
 * bruta— en una linea chica abajo. No se perdio ningun dato: se dejo de repetir.
 *
 * LOS DOS IMPORTES VAN CON CHIP
 * -----------------------------
 * Estan uno al lado del otro y son monedas distintas: el chip ARS/USD es lo que
 * hace que no se lean como una suma (MASTER §3.4). Antes lo daba el encabezado
 * de cada tarjeta ("Valor del stock en pesos"), que ya no existe.
 *
 * NO ES UN PIE DE TABLA
 * ---------------------
 * No sale de sumar una columna a la vista: la suma la hace la base sobre el
 * inventario entero, y la tabla esta paginada (06 §3.5).
 */
export function CintaValor({
  totales,
  cotizacion,
  alcance,
  onQuitarAlcance,
  onVerSinStock,
}: {
  totales: TotalesStock;
  cotizacion: number;
  /** La seccion elegida, si hay una: estos numeros son los de ella y no los del local. */
  alcance?: string;
  /** Saca el filtro de seccion. Con el desglose plegado es la unica salida visible. */
  onQuitarAlcance?: () => void;
  /** Filtra la tabla a los negativos: lo vendido sin stock. */
  onVerSinStock?: () => void;
}): JSX.Element {
  const ganancia = gananciaPotencial(totales);

  return (
    <Card as="section" padding="compacto" className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="flex items-center gap-2">
          {/* `as="h2"`: es una seccion de la pagina y el h1 lo pone el
              PageHeader. El nivel «etiqueta» es el tamaño, no la jerarquia. */}
          <TituloSeccion nivel="etiqueta" as="h2">
            Valor del stock
          </TituloSeccion>
          {/*
            La explicacion va detras de la `i`, como en Caja: los dos numeros
            grandes se leen solos y el parrafo ocupaba dos renglones fijos.
          */}
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Boton crudo: disparador al lado de una etiqueta de 12px,
                  donde IconButton (32px) pesaria mas que el titulo. */}
              <button
                type="button"
                aria-label="Qué significan estos números"
                className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Info className="size-3.5" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm font-normal leading-relaxed">
              Es <strong className="font-medium">el mismo stock</strong> leído en pesos y en dólares
              {cotizacion > 0 ? (
                <>
                  {" "}
                  a US$ 1 = <span className="tabular-nums">$ {formatearNumero(cotizacion, 4)}</span>
                </>
              ) : null}
              : no se suman. A precio de venta,{" "}
              <Importe monto={totales.ventaArs} moneda="ARS" tamano="sm" codigo={false} /> o{" "}
              <Importe monto={totales.ventaUsd} moneda="USD" tamano="sm" codigo={false} />; vendido
              todo hoy dejaría{" "}
              <Importe monto={ganancia.ars} moneda="ARS" tamano="sm" codigo={false} /> o{" "}
              <Importe monto={ganancia.usd} moneda="USD" tamano="sm" codigo={false} /> de ganancia
              bruta.
            </TooltipContent>
          </Tooltip>
          {/*
            El alcance no es decoracion: con el desglose plegado, este boton es
            lo unico que dice que los numeros son de una seccion y no del local,
            y lo unico que lo deshace.
          */}
          {alcance ? (
            onQuitarAlcance ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onQuitarAlcance}
                title="Volver al local entero"
              >
                {alcance}
                <X aria-hidden="true" />
                <span className="sr-only">— quitar el filtro de sección</span>
              </Button>
            ) : (
              <Badge variant="secondary">{alcance}</Badge>
            )
          ) : null}
        </span>
        {/* Unidades: no es plata, va chico y a la derecha. "con unidades"
            porque el catalogo tiene mas y los que estan en cero no son
            inventario (0081). */}
        <span className="text-xs tabular-nums text-muted-foreground">
          {mostrarCantidad(totales.unidades)} u. en {formatearNumero(totales.productos, 0)}{" "}
          productos con unidades
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <Importe monto={totales.costoArs} moneda="ARS" tamano="xl" className="font-semibold" />
        <Importe monto={totales.costoUsd} moneda="USD" tamano="xl" className="font-semibold" />
      </div>

      {/* Lo vendido sin stock no resta del valor: va aca, aparte, y lleva a
          los negativos, que es donde se arregla (cargando la compra). */}
      {/* Lo a pedido se nombra aparte: no suma al valor del local, pero
          «Ver los negativos» tambien lo lista, y la cinta decia «1 u.» con dos
          renglones en la tabla. */}
      {totales.sinStockUnidades > 0 || totales.sinStockAPedidoUnidades > 0 ? (
        <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
          <span>
            Vendido sin stock:{" "}
            {totales.sinStockUnidades > 0 ? (
              <>
                <span className="tabular-nums">{mostrarCantidad(totales.sinStockUnidades)} u.</span>
                {" · "}
                <Importe
                  monto={totales.sinStockCostoArs}
                  moneda="ARS"
                  tamano="sm"
                  codigo={false}
                />{" "}
                a costo
                {totales.sinStockAPedidoUnidades > 0 ? " · más " : null}
              </>
            ) : null}
            {totales.sinStockAPedidoUnidades > 0 ? (
              <span className="tabular-nums">
                {mostrarCantidad(totales.sinStockAPedidoUnidades)} u. a pedido
              </span>
            ) : null}
          </span>
          {onVerSinStock ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onVerSinStock}
            >
              Ver los negativos
            </Button>
          ) : null}
        </p>
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Desglose por seccion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * En que parte del local esta la plata.
 *
 * Es la pregunta del medio, la que no tenia pantalla: mas gruesa que una fila
 * ("este cable vale tanto") y mas fina que la cinta de arriba ("el local vale
 * tanto"). Es la que se hace antes de decidir que reponer.
 *
 * UN BLOQUE Y NO OCHO TARJETAS SUELTAS
 * ------------------------------------
 * Antes eran ocho botones `flex-1` con el contenido sin envolver: con ocho
 * secciones en una sola fila cada caja quedaba mas angosta que su propio
 * numero y el texto se derramaba encima del vecino. Ahora es UNA tarjeta con
 * una grilla adentro (2 columnas, 4 en pantalla ancha), que es lo que hace que
 * los numeros queden en columna y se puedan comparar de arriba abajo.
 *
 * LA BARRA ES EL DATO
 * -------------------
 * Cada renglon lleva que tajada del inventario es, medida en pesos. El orden
 * NO cambia —es el del mostrador, el mismo de las solapas de Catalogo— porque
 * un mapa que se reordena solo deja de servir para ubicarse; la barra da el
 * ranking sin mover nada de lugar.
 *
 * LOS NUMEROS LOS SUMA LA BASE
 * ----------------------------
 * Vienen de `v_stock_por_familia`, que suma sobre el inventario entero (0078),
 * y no de sumar la columna «Vale» de una tabla paginada.
 *
 * CADA RENGLON ES UN FILTRO
 * -------------------------
 * Tocar «Perfumes» filtra la tabla a perfumes Y cambia los totales de arriba.
 * Es el unico selector de seccion de la pantalla: el desplegable «Sección» que
 * habia en la barra de filtros hacia exactamente lo mismo, dos centimetros mas
 * abajo.
 *
 * Y VIENE PLEGADO
 * ---------------
 * Es la pregunta que se hace antes de comprar, no la de todos los dias. Abierto
 * siempre, ocupaba ocho cajas con barras entre la cinta de valor y la tabla, y
 * empujaba el primer producto abajo del pliegue de la pantalla. Cerrado ocupa un
 * renglon que dice que hay adentro, y el que va a comprar lo abre.
 *
 * Cuando hay una seccion elegida se abre solo: con el bloque cerrado, nada
 * explicaria por que la tabla trae la cuarta parte de las filas. La otra mitad
 * de esa promesa la cumple la cinta, que muestra cual es y como sacarla.
 */
export function DesglosePorSeccion({
  filas,
  seccion,
  onSeccion,
}: {
  filas: ReadonlyArray<StockPorFamilia>;
  seccion: "" | Seccion;
  onSeccion: (s: "" | Seccion) => void;
}): JSX.Element | null {
  const porSeccion = plataPorSeccion(filas);

  // «A pedido» no va: lo que es a pedido no esta en el local, asi que su caja
  // solo podia decir cero o, con una venta que dejo el stock en negativo, un
  // importe en rojo que no es plata en ningun lado. Se ve en su solapa de
  // Catalogo.
  const renglones = SECCIONES.filter((x) => x.clave !== "apedido")
    .map((x) => ({ ...x, datos: porSeccion.get(x.clave) }))
    .filter((x) => x.datos !== undefined && x.datos.productos > 0);

  if (renglones.length === 0) return null;

  // La tajada se mide en pesos porque es la moneda en la que esta todo leido a
  // la vez; en dolares daria lo mismo (es la misma conversion para todos).
  const totalArs = renglones.reduce((suma, x) => suma + (x.datos?.costoArs ?? 0), 0);

  return (
    <Desplegable
      abiertoInicial={seccion !== ""}
      forzarAbierto={seccion !== ""}
      resumen={
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          <span>En qué está la plata</span>
          <span className="text-xs font-normal text-muted-foreground">
            por sección del mostrador; tocá una para filtrar
          </span>
        </span>
      }
    >
      {/* Cada seccion es una caja con borde y aire entre cajas: pegadas y sin
          borde, el importe de una quedaba al lado del nombre de la siguiente y
          no se sabia donde terminaba cada boton. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {renglones.map((x) => {
          const activa = seccion === x.clave;
          const ars = x.datos?.costoArs ?? 0;
          const tajada = totalArs > 0 ? (ars / totalArs) * 100 : 0;
          return (
            <Button
              key={x.clave}
              type="button"
              variant="outline"
              aria-pressed={activa}
              onClick={() => onSeccion(activa ? "" : x.clave)}
              className={cn(
                "h-auto flex-col items-stretch gap-1.5 rounded-lg p-3 text-left font-normal sm:h-auto",
                activa ? "border-primary bg-accent ring-1 ring-primary" : undefined,
              )}
            >
              {/* `min-w-0` + `shrink-0`: el nombre largo se corta con
                    puntos suspensivos y el importe nunca se aplasta. Es lo que
                    faltaba antes, cuando el numero se salia de la caja. */}
              <span className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-sm font-medium">{x.label}</span>
                <Importe monto={ars} moneda="ARS" codigo={false} className="shrink-0" />
              </span>
              <span className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                <span className="min-w-0 truncate tabular-nums">
                  {/* Una seccion en negativo no es una tajada: se muestra 0 %, no «-0 %». */}
                  {formatearNumero(Math.max(tajada, 0), 0)}% · {x.datos?.productos ?? 0} prod.
                </span>
                <Importe
                  monto={x.datos?.costoUsd ?? 0}
                  moneda="USD"
                  tamano="sm"
                  codigo={false}
                  className="shrink-0"
                />
              </span>
              <Progreso valor={Math.max(tajada, 0)} className="mt-1 h-1" aria-hidden="true" />
            </Button>
          );
        })}
      </div>
    </Desplegable>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabla
// ─────────────────────────────────────────────────────────────────────────────

export interface FiltrosStockUi {
  texto: string;
  /**
   * La seccion del mostrador: «Accesorios», «Sellados», «Usados».
   *
   * Es el filtro GRUESO y el que se elige primero, porque es como se piensa el
   * local. La familia quedo abajo y adentro: es para afinar una vez elegida la
   * seccion —los Mac dentro de Apple— y no para empezar.
   */
  seccion: "" | Seccion;
  rubro: string;
  estado: "" | EstadoStock;
}

export const FILTROS_STOCK_INICIALES: FiltrosStockUi = {
  texto: "",
  seccion: "",
  rubro: "",
  estado: "",
};

export interface TablaStockProps {
  filas: ReadonlyArray<FilaStock>;
  rubros: ReadonlyArray<Rubro>;
  filtros: FiltrosStockUi;
  onFiltros: (f: FiltrosStockUi) => void;
  /**
   * Cuantos productos hay en cada estado, sobre todo el local. Los chips viven
   * en la barra de filtros y no arriba de ella: son un filtro mas, como el
   * buscador y la familia (06 §1.4).
   */
  cuenta: Record<EstadoStock, number>;
  cargando?: boolean;
  /** El `PieDeLista`: cuantos productos se trajeron de cuantos hay. */
  pie?: ReactNode;
  /** Contar es lo primero que se hace con una fila rara: puede que no falte nada. */
  onContar?: (productoId: number) => void;
  /** Un negativo se arregla cargando el ingreso que falto, no escribiendo el numero. */
  onIngresar?: (productoId: number) => void;
  /**
   * Abre la ficha del producto. El nombre es el link: los umbrales que esta
   * tabla MUESTRA (`min 0 · ideal 0`) se editan alla, y sin este puente
   * arreglarlos era salir de Inventario, entrar a Catalogo, buscar el producto de
   * nuevo y recien ahi tocarlo.
   *
   * Va en el nombre y no en un tercer icono a proposito: la fila ya tiene dos
   * y la regla es no pasar de dos (06 §3.3).
   */
  onAbrirFicha?: (productoId: number) => void;
}

function hayFiltros(f: FiltrosStockUi): boolean {
  return f.texto.trim() !== "" || f.seccion !== "" || f.rubro !== "" || f.estado !== "";
}

export function TablaStock({
  filas,
  rubros,
  filtros,
  onFiltros,
  cuenta,
  cargando = false,
  pie,
  onContar,
  onIngresar,
  onAbrirFicha,
}: TablaStockProps): JSX.Element {
  const vacio = filas.length === 0;
  // Sin filtros puestos, "con estos filtros" no explica nada: lo que pasa es
  // que no hay mercaderia, no que la busqueda no empato.
  const mensajeVacio = hayFiltros(filtros)
    ? "No hay productos con estos filtros."
    : "No hay ningún producto con unidades en el local.";

  /*
    Las familias que se ofrecen dependen de la seccion elegida.

    Sin seccion, todas. Con una seccion que se define por familia, las suyas.
    Y con «Sellados» o «Usados» —que no se definen por familia sino por lo que
    el producto ES— ninguna: el select queda apagado en vez de ofrecer una
    combinacion que siempre daria cero filas.
  */
  const deLaSeccion = rubrosDeSeccion(filtros.seccion);
  const familias =
    filtros.seccion === ""
      ? rubros
      : deLaSeccion.length === 0
        ? []
        : rubros.filter((r) => deLaSeccion.includes(r.nombre));
  const conAcciones = Boolean(onContar || onIngresar);
  const columnas = conAcciones ? 6 : 5;

  const limpiar = hayFiltros(filtros) ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => onFiltros(FILTROS_STOCK_INICIALES)}
    >
      Limpiar filtros
    </Button>
  ) : undefined;

  // Dos iconos, nunca mas (06 §3.3). Con nombre: "Contar CABLE VANWARD".
  const acciones = (f: FilaStock): JSX.Element | null =>
    conAcciones ? (
      <span className="flex justify-end gap-1">
        {onContar ? (
          <IconButton
            icono={ClipboardCheck}
            tamano="sm"
            aria-label={`Contar ${nombreCompleto(f)}`}
            title="Contar"
            onClick={() => onContar(f.id)}
          />
        ) : null}
        {/* Un usado es un teléfono: no se le suman unidades. Entra con «Cargar usado». */}
        {onIngresar && !f.es_usado ? (
          <IconButton
            icono={ArrowDownToLine}
            tamano="sm"
            aria-label={`Ingresar ${nombreCompleto(f)}`}
            title="Ingresar"
            onClick={() => onIngresar(f.id)}
          />
        ) : null}
      </span>
    ) : null;

  return (
    <section className="space-y-3">
      {/*
          UNA barra y una sola fila (06 §1.4): buscar, afinar por familia y los
          chips de estado. Antes esto eran tres bandas apiladas —tres selects
          con etiqueta arriba, los chips en otra fila y un renglon "11 de 380
          productos"— y la tabla empezaba a mitad de pantalla.

          La seccion no esta aca: se elige en «En qué está la plata», que hacia
          lo mismo y ademas muestra cuanta plata hay en cada una.
      */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <InputBusqueda
            id="stock-buscar"
            valor={filtros.texto}
            onValorChange={(texto) => onFiltros({ ...filtros, texto })}
            etiqueta="Buscar"
            placeholder="Buscar un producto"
            className="w-full min-w-[12rem] sm:w-auto sm:flex-1"
          />

          <Select
            value={filtros.rubro}
            onValueChange={(v) => onFiltros({ ...filtros, rubro: v === "_" ? "" : v })}
            disabled={familias.length === 0}
          >
            <SelectTrigger
              id="stock-rubro"
              aria-label="Familia"
              title={
                familias.length === 0
                  ? "Esta sección no se divide por familia"
                  : "Afinar dentro de la sección"
              }
              className="w-full sm:w-44"
            >
              <SelectValue placeholder="Familia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Todas las familias</SelectItem>
              {familias.map((r) => (
                <SelectItem key={r.id} value={r.nombre}>
                  {r.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ChipsEstado
            cuenta={cuenta}
            estado={filtros.estado}
            onEstado={(estado) => onFiltros({ ...filtros, estado })}
            className="sm:ml-auto"
          />

          {hayFiltros(filtros) ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onFiltros(FILTROS_STOCK_INICIALES)}
            >
              Limpiar
            </Button>
          ) : null}
        </div>

        {/* La ayuda del estado elegido: un negativo parece un error del sistema
            y no lo es, asi que se explica donde se lo filtra. */}
        {filtros.estado !== "" ? (
          <Aviso tono="neutral" compacto icono={TriangleAlert}>
            {AYUDA_ESTADO[filtros.estado]}
          </Aviso>
        ) : null}
      </div>

      {/* Desktop: tabla con una sola valorizacion visible, en la moneda del costo. */}
      <Card padding="none" className="hidden overflow-hidden md:block">
        <Table densidad="compacta" alto="acotado">
          <TableHeader>
            <TableRow>
              <TableHead>Producto</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Costo unit.</TableHead>
              <TableHead className="text-right">Vale</TableHead>
              {conAcciones ? (
                <TableHead className="w-20">
                  <span className="sr-only">Acciones</span>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {vacio ? (
              cargando ? (
                <tr>
                  <td colSpan={columnas}>
                    <Cargando texto="Buscando…" />
                  </td>
                </tr>
              ) : (
                <TableVacia colSpan={columnas} mensaje={mensajeVacio} accion={limpiar} />
              )
            ) : (
              filas.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="max-w-[20rem]">
                    {/* El nombre se ARMA: color, memoria y bateria salieron de
                        adentro del nombre y sin ellos dos unidades del mismo
                        modelo se leen igual (ver `nombre.ts`). El `title` deja
                        el completo a mano cuando la celda lo corta. */}
                    {onAbrirFicha ? (
                      <Button
                        type="button"
                        variant="link"
                        className="block max-w-full truncate text-left text-sm text-foreground"
                        title={`${nombreCompleto(f)} — abrir la ficha`}
                        onClick={() => onAbrirFicha(f.id)}
                      >
                        {nombreCompleto(f)}
                      </Button>
                    ) : (
                      <span className="block truncate text-sm" title={nombreCompleto(f)}>
                        {nombreCompleto(f)}
                      </span>
                    )}
                    {/* Familia y proveedor bajo el nombre: no son columnas (06 §3.6). */}
                    <span className="block truncate text-xs text-muted-foreground">
                      <Codigo>{f.codigo_interno}</Codigo>
                      {f.rubro ? ` · ${f.rubro}` : ""}
                      {f.proveedor ? ` · ${f.proveedor}` : ""}
                    </span>
                  </TableCell>
                  <TableCellImporte>
                    <Unidades cantidad={aNumero(f.stock_actual)} />
                    <span className="block whitespace-nowrap text-xs text-muted-foreground">
                      mín {formatearNumero(aNumero(f.stock_minimo), 0)} · ideal{" "}
                      {formatearNumero(aNumero(f.stock_ideal), 0)}
                    </span>
                  </TableCellImporte>
                  <TableCell>
                    <Badge variant={VARIANTE_ESTADO[f.estado]}>{ETIQUETA_ESTADO[f.estado]}</Badge>
                  </TableCell>
                  {/* Las dos columnas de plata mezclan monedas: chip en cada fila. */}
                  <TableCellImporte monto={aNumero(f.costo)} moneda={f.costo_moneda} />
                  <TableCellImporte>
                    <Valorizacion fila={f} />
                  </TableCellImporte>
                  {conAcciones ? <TableCell>{acciones(f)}</TableCell> : null}
                </TableRow>
              ))
            )}
            {!vacio && pie ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnas}>{pie}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile: tarjetas-registro. La barra lateral es la moneda del costo,
          que es la moneda "propia" del producto; las dos valorizaciones van
          con chip porque conviven en la misma linea. */}
      <div className="flex flex-col gap-2 md:hidden">
        {vacio ? (
          cargando ? (
            <Cargando texto="Buscando…" />
          ) : (
            <Card padding="none">
              <EstadoVacio tamano="sm" titulo={mensajeVacio} accion={limpiar} />
            </Card>
          )
        ) : (
          filas.map((f) => (
            <Card
              key={f.id}
              as="article"
              padding="compacto"
              acento={f.costo_moneda}
              className="flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {onAbrirFicha ? (
                    <Button
                      type="button"
                      variant="link"
                      className="block h-auto max-w-full whitespace-normal break-words text-left text-sm font-medium text-foreground sm:h-auto sm:truncate"
                      title={`${nombreCompleto(f)} — abrir la ficha`}
                      onClick={() => onAbrirFicha(f.id)}
                    >
                      {nombreCompleto(f)}
                    </Button>
                  ) : (
                    <p
                      className="break-words text-sm font-medium sm:truncate"
                      title={nombreCompleto(f)}
                    >
                      {nombreCompleto(f)}
                    </p>
                  )}
                  <p className="truncate text-xs text-muted-foreground">
                    <Codigo>{f.codigo_interno}</Codigo>
                    {f.rubro ? ` · ${f.rubro}` : ""}
                  </p>
                </div>
                <Badge variant={VARIANTE_ESTADO[f.estado]} className="shrink-0">
                  {ETIQUETA_ESTADO[f.estado]}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <Unidades cantidad={aNumero(f.stock_actual)} />
                <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Importe monto={aNumero(f.valor_costo_ars)} moneda="ARS" tamano="sm" />
                  <Importe monto={aNumero(f.valor_costo_usd)} moneda="USD" tamano="sm" />
                </span>
              </div>
              {conAcciones ? acciones(f) : null}
            </Card>
          ))
        )}
        {!vacio ? pie : null}
      </div>
    </section>
  );
}

/**
 * "Vale" en la moneda del costo del producto, que es su lectura propia. La otra
 * moneda va debajo en chico: no se pierde y no suma una columna. Las dos llevan
 * chip porque la columna mezcla monedas fila a fila (MASTER §3.4).
 */
function Valorizacion({ fila }: { fila: FilaStock }): JSX.Element {
  const enPesos = fila.costo_moneda === "ARS";
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <Importe
        monto={aNumero(enPesos ? fila.valor_costo_ars : fila.valor_costo_usd)}
        moneda={fila.costo_moneda}
      />
      <Importe
        monto={aNumero(enPesos ? fila.valor_costo_usd : fila.valor_costo_ars)}
        moneda={enPesos ? "USD" : "ARS"}
        tamano="sm"
      />
    </span>
  );
}

export function Unidades({ cantidad }: { cantidad: number }): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap text-sm tabular-nums",
        cantidad < 0
          ? "font-medium text-destructive"
          : cantidad === 0
            ? "text-muted-foreground"
            : "",
      )}
    >
      {cantidad < 0 ? <PackageX className="size-3.5" aria-hidden="true" /> : null}
      {mostrarCantidad(cantidad)} u.
    </span>
  );
}
